import { CircleQuestionMark, FolderOpen, Mic, Settings } from 'lucide-react'
import * as React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BottomBarButton } from '@/components'
import { RecordProjectPickerSheet } from '@/components/sidebar/RecordProjectPickerSheet'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export interface BottomBarProps extends React.HTMLAttributes<HTMLDivElement> {
	currentPage?: string
}

const BottomBar = React.forwardRef<HTMLDivElement, BottomBarProps>(
	({ currentPage, ...props }, ref) => {
		const { t } = useTranslation()
		const navigate = useNavigate()
		const { projectId } = useParams<{ projectId?: string }>()
		const [isPickerOpen, setIsPickerOpen] = useState(false)

		const handleRecordClick = () => {
			const id = projectId?.trim()
			if (id) {
				void navigate(`/projects/${id}?openRecorder=1`)
				return
			}
			setIsPickerOpen(true)
		}

		return (
			<>
				<nav
					ref={ref}
					className={cn(
						'md:hidden fixed bottom-0 left-0 right-0 z-50 grid grid-cols-5 items-end gap-1 border-t border-gray-200 bg-white px-2 pt-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] text-gray-700 font-medium shadow-md transition-all duration-300'
					)}
					{...props}
				>
					<div className="flex justify-center pb-1">
						<BottomBarButton
							label={t('navigation.projects')}
							icon={<FolderOpen size={40} />}
							currentPage={currentPage}
							href="/projects"
						/>
					</div>

					<span className="min-w-0" aria-hidden="true" />

					<div className="-mt-6 flex flex-col items-center justify-end pb-0 z-[55] pointer-events-none">
						<button
							type="button"
							onClick={handleRecordClick}
							className={cn(
								'pointer-events-auto flex h-16 w-16 shrink-0 cursor-pointer items-center justify-center rounded-full',
								'bg-brand-accent text-white shadow-lg ring-4 ring-brand-accent/25',
								'transition hover:bg-brand-accent-hover active:scale-[0.98]'
							)}
							aria-label={t('navigation.record')}
						>
							<Mic className="h-9 w-9" aria-hidden />
						</button>
					</div>

					<div className="flex justify-center pb-1">
						<BottomBarButton
							label={t('navigation.settings')}
							icon={<Settings size={40} />}
							currentPage={currentPage}
							href="/settings"
						/>
					</div>

					<div className="flex justify-center pb-1">
						<BottomBarButton
							label={t('navigation.help')}
							icon={<CircleQuestionMark size={40} />}
							currentPage={currentPage}
							href="/help"
						/>
					</div>
				</nav>

				<RecordProjectPickerSheet open={isPickerOpen} onOpenChange={setIsPickerOpen} />
			</>
		)
	}
)
BottomBar.displayName = 'BottomBar'

export { BottomBar }
